/* eslint-disable no-shadow */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cx from 'classnames';

import TranslationProvider from '../../providers/TranslationProvider';
import Form from './Form';
import Label from './Label';
import FormErrorInline from './FormErrorInline';
import { RESY_WEB_DEPRECATE_EMAIL_PASSWORD_LOGIN } from '../../constants/optimizelyExperiments';

import {
  FirstNameInput,
  LastNameInput,
  PhoneInput,
  EmailInput,
  PasswordInput,
  PasswordCriteria,
} from '../Input';
import Spacer from '../Spacer';
import AriaLiveRegion from '../AriaLiveRegion';
import { SubmitButton } from '../Button';
import { TermsOfServiceCheckbox, MarketingOptInCheckbox } from '../Checkbox';
import containerQueryProps from '../../props/containerQueryProps';
import { resetRegistration } from '../../reducers/userSlice';
import {
  getCanonicalPhoneErrorMessage,
  getPhoneValidationError,
} from '../../utils/phoneValidation';

import './Form.scss';

const CAPTCHA_CONTAINER_CLASS = 'captcha__container';

class SignupForm extends React.Component {
  state = {
    passwordCriteriaOpen: false,
    captchaVerified: false,
    phoneValidationError: null,
    disableContinue: false,
    hasEditedPhoneSinceSubmit: false,
  }

  componentDidMount() {
    const { AutomationMode, grecaptcha } = this.props;

    if (!AutomationMode) {
      this.captchaWidgetId = grecaptcha.render(CAPTCHA_CONTAINER_CLASS, {
        sitekey: '6Lfw-dIZAAAAAESRBH4JwdgfTXj5LlS1ewlvvCYe',
        tabindex: 0,
        callback: this.captchaCallback,
        'expired-callback': this.captchaExpired,
        'error-callback': this.captchaExpired,
      });
      const captchaIframe = document.querySelector("iframe[title='reCAPTCHA']");
      if (captchaIframe) captchaIframe.tabIndex = '0';
    }
  }

  componentWillUnmount() {
    const { resetRegistration } = this.props;
    resetRegistration();
  }

  captchaCallback = (response) => {
    this.setState({ captchaVerified: !!response });
  }

  // On expiry/error the reCAPTCHA token is no longer valid; clear the checkbox so
  // the user is prompted to re-verify instead of being stuck with a checked box
  // and a permanently disabled submit button.
  captchaExpired = () => {
    const { grecaptcha } = this.props;
    this.setState({ captchaVerified: false });
    if (grecaptcha && this.captchaWidgetId !== undefined) {
      grecaptcha.reset(this.captchaWidgetId);
    }
  }

  email = (formArgs) => {
    const {
      errorKeys,
      onChange,
    } = formArgs;

    const {
      email,
      errorData,
    } = this.props;

    if (email) { return null; }

    return (
      <>
        <Label forInput="email" required>
          <TranslationProvider
            translationKey="email"
          >
            Email
          </TranslationProvider>
        </Label>
        <EmailInput
          ariaRequired
          onChange={onChange}
          errors={errorKeys}
          value={email}
          ariaDescribedby="email_error"
          data-testid="sign_up_email"
        />
        { errorData
          && errorData.em_address
          && <FormErrorInline id="email_error">{errorData.em_address}</FormErrorInline>}
      </>
    );
  }

  name = (formArgs) => {
    const {
      errorKeys,
      onChange,
    } = formArgs;

    const {
      containerQuery: { sm },
      errorData,
      firstName,
      lastName,
    } = this.props;

    return (
      <>
        <div className={cx('SignupForm__Name', {
          'SignupForm__Name--sm': sm,
        })}
        >
          <div>
            <Label forInput="first_name" required>
              <TranslationProvider
                translationKey="first_name"
              >
                First Name
              </TranslationProvider>
            </Label>
            <FirstNameInput
              ariaRequired
              onChange={onChange}
              errors={errorKeys}
              value={firstName}
              ariaDescribedby="firstname_error"
            />
          </div>
          {errorData?.first_name && <FormErrorInline id="firstname_error">{errorData.first_name}</FormErrorInline>}
          <div>
            <Label forInput="last_name" required>
              <TranslationProvider
                translationKey="last_name"
              >
                Last Name
              </TranslationProvider>
            </Label>
            <LastNameInput
              ariaRequired
              onChange={onChange}
              errors={errorKeys}
              value={lastName}
              ariaDescribedby="lastname_error"
            />
          </div>
        </div>
        {errorData?.last_name && <FormErrorInline id="lastname_error">{errorData.last_name}</FormErrorInline>}
      </>
    );
  }

  onBlur = (e) => {
    this.setState({
      passwordCriteriaOpen: !!e.currentTarget.value.length,
    });
  }

  onFocus = () => {
    this.setState({
      passwordCriteriaOpen: true,
    });
  }

  phone = (formArgs = {}) => {
    const {
      errorKeys = [],
      onChange,
    } = formArgs;

    const {
      countryCodes,
      defaultCountryCode,
      errorData,
      mobile,
      phone,
      venue,
    } = this.props;

    if (phone) { return null; }

    const hasServerPhoneError = !!(errorData?.mobile_number);
    const { hasEditedPhoneSinceSubmit, phoneValidationError } = this.state;
    const shouldShowServerPhoneError = hasServerPhoneError && !hasEditedPhoneSinceSubmit;
    const phoneErrorRaw = phoneValidationError
      || (shouldShowServerPhoneError ? errorData.mobile_number : null);
    const phoneError = phoneErrorRaw ? getCanonicalPhoneErrorMessage() : null;
    const nonPhoneErrorKeys = errorKeys.filter((key) => key !== 'phone' && key !== 'country_code');
    const phoneInputErrors = phoneError
      ? [...nonPhoneErrorKeys, 'phone', 'country_code']
      : nonPhoneErrorKeys;

    return (
      <>
        <Label forInput="phone" required>
          <TranslationProvider
            translationKey="mobile_number"
          >
            Phone
          </TranslationProvider>
        </Label>
        <PhoneInput
          ariaRequired
          onChange={this.onFormChange(onChange)}
          countryCodes={countryCodes}
          defaultCountryCode={defaultCountryCode}
          errors={phoneInputErrors}
          phone={mobile}
          ariaDescribedby="phone_error phone_footnote"
        />
        {phoneError && <FormErrorInline id="phone_error">{phoneError}</FormErrorInline>}
        <TranslationProvider
          translationKey={venue ? 'onboarding.footnote' : 'onboarding.footnote_secondary'}
          translationValues={{
            venueName: venue,
          }}
        >
          <div className="Form__Footnote" id="phone_footnote">
            Resy will only use this number to confirm and manage your reservation.
          </div>
        </TranslationProvider>
      </>
    );
  }

  password = (formArgs) => {
    const {
      errorKeys, onChange, values,
    } = formArgs;
    const {
      errorData, passwordRequired, isPasswordDeprecateFlagOn,
    } = this.props;
    const {
      passwordCriteriaOpen,
    } = this.state;

    if (!passwordRequired || isPasswordDeprecateFlagOn) { return null; }

    return (
      <>
        <Label forInput="password" required>
          <TranslationProvider
            translationKey="password"
          >
            Password
          </TranslationProvider>
        </Label>
        <PasswordInput
          ariaRequired
          type="new"
          onBlur={this.onBlur}
          onChange={onChange}
          onFocus={this.onFocus}
          errors={errorKeys}
          confirmPassword={false}
          ariaDescribedby="password_error"
        />
        { errorData
          && errorData.password
          && <FormErrorInline id="password_error">{errorData.password}</FormErrorInline>}
        <PasswordCriteria password={values.password} virgin={!errorKeys.length} open={passwordCriteriaOpen} />
      </>
    );
  }

  requiredFields = () => {
    const {
      passwordRequired, phone, email, isPasswordDeprecateFlagOn,
    } = this.props;
    const fields = ['first_name', 'last_name', 'terms_of_service'];
    if (passwordRequired && !isPasswordDeprecateFlagOn) {
      fields.push('password');
    }
    if (!phone) {
      fields.push('phone');
    }
    if (!email) {
      fields.push('email');
    }
    return fields;
  }

  shouldValidatePhoneField = () => {
    const { phone } = this.props;
    return !phone;
  }

  onFormChange = (onChange) => (values) => {
    if (!this.shouldValidatePhoneField()) {
      onChange(values);
      return;
    }

    const nextPhoneValidationError = getPhoneValidationError(
      values,
      { fieldName: 'phone' }
    );
    this.setState({
      phoneValidationError: nextPhoneValidationError,
      disableContinue: !!nextPhoneValidationError,
      hasEditedPhoneSinceSubmit: true,
    });
    onChange(values);
  }

  onFormSubmit = async (values) => {
    const { onSubmit } = this.props;
    const shouldValidatePhoneField = this.shouldValidatePhoneField();

    if (shouldValidatePhoneField) {
      this.setState({ hasEditedPhoneSinceSubmit: false });
      const phoneError = getPhoneValidationError(
        values,
        { validateIncomplete: true, fieldName: 'phone' }
      );
      if (phoneError) {
        this.setState({
          phoneValidationError: phoneError,
          disableContinue: true,
        });
        throw { data: { mobile_number: phoneError } }; // eslint-disable-line no-throw-literal
      }
    } else {
      this.setState({
        phoneValidationError: null,
        disableContinue: false,
      });
    }

    this.setState({
      phoneValidationError: null,
      disableContinue: false,
    });
    try {
      await onSubmit(values);
    } catch (err) {
      const serverPhoneError = err?.data?.data?.mobile_number || err?.data?.mobile_number;
      if (serverPhoneError && shouldValidatePhoneField) {
        this.setState({ disableContinue: true });
      }
      throw err;
    }
  }

  render() {
    const {
      errorMessage,
      cta,
      datadogActionName,
      fullBleedBtn,
      error,
      isNonUS,
      AutomationMode,
    } = this.props;
    const {
      captchaVerified,
      disableContinue,
    } = this.state;

    return (
      <div className="SignupForm">
        <Form
          name="signup_form"
          onSubmit={this.onFormSubmit}
          error={error}
          requiredFields={this.requiredFields()}
          datadogActionName={datadogActionName}
        >
          {(formArgs) => {
            const {
              disabled, errorKeys, onChange, processing,
            } = formArgs;

            return (
              <div>
                <AriaLiveRegion id="SignupForm__status">
                  {processing ? 'Loading' : ''}
                </AriaLiveRegion>

                {this.name(formArgs)}
                {this.phone(formArgs)}
                {this.email(formArgs)}
                {this.password(formArgs)}

                <Spacer height={10} />

                <MarketingOptInCheckbox
                  onChange={onChange}
                  errors={errorKeys}
                  isNonUS={isNonUS}
                />

                <TermsOfServiceCheckbox
                  onChange={onChange}
                  errors={errorKeys}
                  isNonUS={isNonUS}
                />

                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                <div className="SignupForm__captcha-container" tabIndex="0" id={CAPTCHA_CONTAINER_CLASS} />

                {errorMessage && <div key={errorMessage} className="alert" role="alert" aria-live="assertive">{errorMessage}</div>}

                <hr />
                <TranslationProvider
                  translationKey="onboarding.create_account_cta"
                >
                  <SubmitButton
                    processing={processing}
                    fullBleed={fullBleedBtn}
                    disabled={disabled || disableContinue || !(captchaVerified || AutomationMode)}
                    className="Button--signup"
                    data-dd-action-name={datadogActionName}
                    lg
                  >
                    {cta}
                  </SubmitButton>
                </TranslationProvider>
              </div>
            );
          }}
        </Form>
      </div>
    );
  }
}

SignupForm.propTypes = {
  AutomationMode: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cta: PropTypes.string,
  datadogActionName: PropTypes.string,
  countryCodes: PropTypes.arrayOf(PropTypes.shape({
    iso3166: PropTypes.string,
    code: PropTypes.string,
    country_name: PropTypes.string,
  })),
  defaultCountryCode: PropTypes.string,
  grecaptcha: PropTypes.shape({
    render: PropTypes.func.isRequired,
    reset: PropTypes.func,
  }).isRequired,
  errorMessage: PropTypes.string,
  phone: PropTypes.string,
  fullBleedBtn: PropTypes.bool,
  isPasswordDeprecateFlagOn: PropTypes.bool,
  venue: PropTypes.string,
  containerQuery: containerQueryProps,
  passwordRequired: PropTypes.bool,
  error: PropTypes.shape({
    serverError: PropTypes.bool,
    msg: PropTypes.string,
    keys: PropTypes.arrayOf(PropTypes.string),
  }),
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  mobile: PropTypes.string,
  isNonUS: PropTypes.bool,
  errorData: PropTypes.shape({
    em_address: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    mobile_number: PropTypes.string,
    password: PropTypes.string,
  }).isRequired,
  resetRegistration: PropTypes.func.isRequired,
};

SignupForm.defaultProps = {
  cta: 'Create Account',
  datadogActionName: undefined,
  countryCodes: undefined,
  defaultCountryCode: '1',
  errorMessage: undefined,
  phone: '',
  fullBleedBtn: false,
  isPasswordDeprecateFlagOn: false,
  venue: null,
  containerQuery: {},
  passwordRequired: true,
  error: {
    serverError: false,
    msg: '',
    keys: [],
  },
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  isNonUS: undefined,
};

const mapStateToProps = ({ user, experiments }) => ({
  errorData: user.registrationError,
  isPasswordDeprecateFlagOn: experiments?.[RESY_WEB_DEPRECATE_EMAIL_PASSWORD_LOGIN]?.enabled,
});
const mapDispatchToProps = {
  resetRegistration,
};

export { SignupForm as ComponentUnderTest };

export default connect(mapStateToProps, mapDispatchToProps)(SignupForm);
